import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { collectionsAPI, productsAPI } from '../services/api';
import { useAuth } from './AuthContext';

const AppContext = createContext();

const initialState = {
  collections: [],
  products: [],
  loading: {
    collections: true,
    products: true
  },
  error: null
};

function appReducer(state, action) {
  switch (action.type) {
    // Loading states
    case 'SET_LOADING':
      return {
        ...state,
        loading: { ...state.loading, [action.payload.type]: action.payload.loading }
      };
    
    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload
      };
    
    // Collections
    case 'SET_COLLECTIONS':
      return {
        ...state,
        collections: action.payload,
        loading: { ...state.loading, collections: false }
      };
    
    case 'ADD_COLLECTION':
      return {
        ...state,
        collections: [...state.collections, action.payload]
      };
    
    case 'UPDATE_COLLECTION':
      return {
        ...state,
        collections: state.collections.map(collection =>
          collection.id === action.payload.id ? action.payload : collection
        )
      };
    
    case 'DELETE_COLLECTION':
      return {
        ...state,
        collections: state.collections.filter(collection => collection.id !== action.payload),
        products: state.products.filter(product => product.collection_id !== action.payload)
      };
    
    // Products
    case 'SET_PRODUCTS':
      return {
        ...state,
        products: action.payload,
        loading: { ...state.loading, products: false }
      };
    
    case 'ADD_PRODUCT':
      return {
        ...state,
        products: [...state.products, action.payload]
      };
    
    case 'UPDATE_PRODUCT':
      return {
        ...state,
        products: state.products.map(product =>
          product.id === action.payload.id ? action.payload : product
        )
      };
    
    case 'DELETE_PRODUCT':
      return {
        ...state,
        products: state.products.filter(product => product.id !== action.payload)
      };
    
    default:
      return state;
  }
}

export const AppProvider = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);
  const { isAuthenticated, isLoading } = useAuth();

  // Load data when user is authenticated
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      console.log('AppContext: User authenticated, loading collections and products...');
      loadCollections();
      loadProducts();
    } else {
      console.log('AppContext: User not authenticated yet, isAuthenticated:', isAuthenticated, 'isLoading:', isLoading);
    }
  }, [isAuthenticated, isLoading]);

  // API functions
  const loadCollections = async () => {
    try {
      console.log('AppContext: Starting to load collections...');
      dispatch({ type: 'SET_LOADING', payload: { type: 'collections', loading: true } });
      const response = await collectionsAPI.getAll();
      console.log('AppContext: Collections API response:', response);
      console.log('AppContext: Collections data:', response.collections);
      dispatch({ type: 'SET_COLLECTIONS', payload: response.collections });
    } catch (error) {
      console.error('Error loading collections:', error);
      console.error('Error details:', error.message);
      dispatch({ type: 'SET_ERROR', payload: error.message });
      dispatch({ type: 'SET_LOADING', payload: { type: 'collections', loading: false } });
    }
  };

  const loadProducts = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: { type: 'products', loading: true } });
      const response = await productsAPI.getAll();
      dispatch({ type: 'SET_PRODUCTS', payload: response.products });
    } catch (error) {
      console.error('Error loading products:', error);
      dispatch({ type: 'SET_ERROR', payload: error.message });
      dispatch({ type: 'SET_LOADING', payload: { type: 'products', loading: false } });
    }
  };

  const addCollection = async (collectionData) => {
    try {
      const response = await collectionsAPI.create(collectionData);
      dispatch({ type: 'ADD_COLLECTION', payload: response.collection });
      return response.collection;
    } catch (error) {
      console.error('Error creating collection:', error);
      throw error;
    }
  };

  const addProduct = async (productData) => {
    try {
      const response = await productsAPI.create(productData);
      dispatch({ type: 'ADD_PRODUCT', payload: response.product });
      // Reload collections to update product counts
      loadCollections();
      return response.product;
    } catch (error) {
      console.error('Error creating product:', error);
      throw error;
    }
  };

  const updateProduct = async (productId, productData) => {
    try {
      const response = await productsAPI.update(productId, productData);
      dispatch({ type: 'UPDATE_PRODUCT', payload: response.product });
      loadCollections();
      return response.product;
    } catch (error) {
      console.error('Error updating product:', error);
      throw error;
    }
  };

  const updateCollection = async (collectionId, collectionData) => {
    try {
      const response = await collectionsAPI.update(collectionId, collectionData);
      dispatch({ type: 'UPDATE_COLLECTION', payload: response.collection });
      return response.collection;
    } catch (error) {
      console.error('Error updating collection:', error);
      throw error;
    }
  };

  const deleteCollection = async (collectionId) => {
    try {
      await collectionsAPI.delete(collectionId);
      dispatch({ type: 'DELETE_COLLECTION', payload: collectionId });
    } catch (error) {
      console.error('Error deleting collection:', error);
      throw error;
    }
  };

  const deleteProduct = async (productId) => {
    try {
      await productsAPI.delete(productId);
      dispatch({ type: 'DELETE_PRODUCT', payload: productId });
      // Reload collections to update product counts
      loadCollections();
    } catch (error) {
      console.error('Error deleting product:', error);
      throw error;
    }
  };

  const getProductsByCollection = (collectionId) => {
    console.log('getProductsByCollection called with collectionId:', collectionId, typeof collectionId);
    console.log('Available products:', state.products.length);
    
    const filtered = state.products.filter(product => {
      const productCollectionId = product.collectionId || product.collection_id;
      const match = productCollectionId === collectionId || 
                   productCollectionId === parseInt(collectionId) ||
                   productCollectionId === String(collectionId);
      
      console.log(`Product "${product.name}" - collectionId: ${productCollectionId} (${typeof productCollectionId}), looking for: ${collectionId} (${typeof collectionId}), match: ${match}`);
      return match;
    });
    
    console.log(`Found ${filtered.length} products for collection ${collectionId}`);
    return filtered;
  };

  const refreshData = async () => {
    console.log('AppContext: Refreshing all data...');
    await Promise.all([loadCollections(), loadProducts()]);
  };

  return (
    <AppContext.Provider value={{
      ...state,
      loadCollections,
      loadProducts,
      refreshData,
      addCollection,
      addProduct,
      updateProduct,
      updateCollection,
      deleteCollection,
      deleteProduct,
      getProductsByCollection
    }}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}; 